import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'

import { Store } from '@ngrx/store'

import { Observable } from 'rxjs'

import { ConversationSelectors } from '@flowx/designer/modules/ai/conversation/conversation.selectors'
import { APP_ID_HEADER } from '@flowx/designer/modules/workspaces/shared/commons.types'
import { selectAppId } from '@flowx/designer/shared/store/selectors/router.selectors'

const AI_API_PREFIX = 'ai/api/'

const CONVERSATION_ID_IN_URL = /\/conversations\/([0-9a-fA-F-]{36})/

@Injectable()
export class AiAppIdInterceptor implements HttpInterceptor {
  #store = inject(Store)
  #appId = this.#store.selectSignal(selectAppId)
  #conversations = this.#store.selectSignal(ConversationSelectors.conversations)

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!request.url.includes(AI_API_PREFIX)) {
      return next.handle(request)
    }

    // The route is only the right source when the call isn't about an existing conversation
    // (creating one). Otherwise the conversation wins: it is the project the request acts on,
    // which is not necessarily the project currently open.
    const appId = this.#appIdFromRequestedConversation(request.url) ?? this.#appId()
    if (!appId) {
      return next.handle(request)
    }

    return next.handle(request.clone({ setHeaders: { [APP_ID_HEADER]: appId } }))
  }

  // Calls that finalise a conversation — discarding an outcome from a leave-guard, closing it —
  // land after the router has already left the project, so the route carries no appId at all.
  #appIdFromRequestedConversation(url: string): string | null {
    const conversationId = url.match(CONVERSATION_ID_IN_URL)?.[1]

    return conversationId
      ? (this.#conversations().find((conversation) => conversation.id === conversationId)
          ?.applicationId ?? null)
      : null
  }
}
